import { useAuth0 } from "@auth0/auth0-react";
import { Dialog, type AlertColor } from "@mui/material";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Outlet, matchPath, useLocation, useParams } from "react-router-dom";
import { ChangelogModal } from "src/components/Changelog";
import { buildDeviceManagementSettingsPath } from "src/components/DeviceManagement/deviceManagementNavigation";
import Welcome from "src/components/Onboarding/Welcome";
import { ROUTES, ROUTE_SLUGS } from "src/config/navigation/routes";
import { useUpdateUserUiStateMutation } from "src/hooks/mutations";
import { useGetDevices, useGetUser } from "src/hooks/queries";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import useUiStateItemLock from "src/hooks/useUiStateItemLock";
import { useStore } from "src/stores";
import { Plan } from "src/stores/models";
import { AssistantProcessStatus } from "src/stores/models/assistant-process";
import { WELCOME_MODAL_KEY } from "src/utils/consts";
import { getSubscription } from "src/utils/entitlements";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { getOrgFromUser } from "src/utils/user";
import { Fallback } from "..";
import { AppVersionUpdatePrompt } from "./AppVersionUpdatePrompt";
import { Banner } from "./Banner";
import { LeftDrawer } from "./LeftDrawer";
import { RightColumn } from "./RightColumn";

type MainLayoutProps = {
  children?: React.ReactNode;
};

const LEFT_DRAWER_COLLAPSED_WIDTH_PX = 46;
const LEFT_DRAWER_DEFAULT_WIDTH_PX = 200;

type LeftDrawerWidthMode = "collapsed" | "reset" | "custom";
type TopBanner = {
  key: string;
  bannerText: string;
  buttonText?: string;
  buttonLink?: string;
  color?: AlertColor;
  rightText?: string;
  onClose?: () => void;
};

type ScheduledBannerConfig = {
  key: string;
  bannerText: string;
  color: AlertColor;
  /** Banner is visible until this UTC timestamp (ms) */
  endTime: number;
  /** localStorage key used to persist dismissal */
  localStorageKey: string;
};

const SCHEDULED_BANNERS: ScheduledBannerConfig[] = [
  {
    key: "maintenance-2026-07-13",
    bannerText:
      "Scheduled maintenance: Monday July 13th, starting 9:00 AM CEST. The app may be temporarily unavailable.",
    color: "info",
    endTime: new Date("2026-07-13T09:00:00Z").getTime(),
    localStorageKey: "maintenance-banner-dismissed-2026-07-13",
  },
];

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

const getFallbackPanelWidth = () =>
  typeof window === "undefined"
    ? 1024
    : Math.max(window.innerWidth, LEFT_DRAWER_DEFAULT_WIDTH_PX * 2);

const getPanelContainerWidth = (container: HTMLDivElement | null) => {
  const measuredWidth = container?.getBoundingClientRect().width ?? 0;
  return Math.max(measuredWidth, getFallbackPanelWidth());
};

const pixelWidthToPercentage = (width: number, containerWidth: number) => {
  if (containerWidth <= 0) {
    return 0;
  }

  return clampPercentage((width / containerWidth) * 100);
};

const percentageToPixelWidth = (percentage: number, containerWidth: number) =>
  (percentage / 100) * containerWidth;

const getBannerHeightValue = (heightCount: number) => {
  if (heightCount <= 0) {
    return "0px";
  }

  if (heightCount === 1) {
    return "var(--banner-height)";
  }

  return `calc(${Array.from(
    { length: heightCount },
    () => "var(--banner-height)"
  ).join(" + ")})`;
};

const RIGHT_PANEL_ORG_ROUTE_PATTERNS = [
  ROUTES.DASHBOARD,
  ROUTES.DOCUMENTS,
  ROUTES.ORGANIZATION_DOCUMENT,
  ROUTES.CHANGE_MANAGEMENT_PLANS,
  ROUTES.CHANGE_MANAGEMENT_PLAN,
  ROUTES.TASKS,
  ROUTES.SKILLS,
  ROUTES.QUALITY_EVENTS,
  ROUTES.QUALITY_EVENT_OVERVIEW,
  ROUTES.QUALITY_EVENT_RECORD,
  ROUTES.TRAININGS,
  ROUTES.TRAINING_ASSIGN,
  ROUTES.TRAINING_EDIT,
  ROUTES.TRAINING_QUIZ_EDIT,
  ROUTES.ORG_TASK_DETAIL,
];

const isRightPanelOrgRoute = (pathname: string) =>
  RIGHT_PANEL_ORG_ROUTE_PATTERNS.some((pattern) =>
    Boolean(matchPath({ path: pattern, end: true }, pathname))
  );

export const MainLayout = observer((props: MainLayoutProps) => {
  const { orgId = "", deviceId = "" } = useParams();
  const { isAuthenticated } = useAuth0();
  const { data: user } = useGetUser(isAuthenticated);
  const org = getOrgFromUser(user, orgId);
  const subscription = org ? getSubscription(org) : null;
  const { itemIsOpen: welcomeModalIsOpen, closeItem: closeWelcomeModal } =
    useUiStateItemLock(WELCOME_MODAL_KEY);
  const patchUserUiStateMutation = useUpdateUserUiStateMutation();

  const currentPath = useLocation().pathname;
  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
    enabled: !!deviceId && !!orgId && !!user,
  });
  const { data: devices, isLoading: isLoadingDevices } = useGetDevices({
    orgId,
    enabled: !!orgId && !!user,
  });

  // when user has no name, even if they have completed the onboarding, we show the welcome modal so they can add their name
  if (user && (!user.firstName || !user.lastName) && !welcomeModalIsOpen) {
    patchUserUiStateMutation.mutate({
      welcomeSeen: false,
    });
  }

  // Can be removed if welcome modal is removed
  const onboardingIsOpen =
    welcomeModalIsOpen || !user?.firstName || !user?.lastName;

  const isAssistantProcessInProgress =
    assistantProcesses?.[0]?.status === AssistantProcessStatus.IN_PROGRESS;
  const shouldShowBannerAssistant =
    isAssistantProcessInProgress &&
    !currentPath.includes("assistant") &&
    !!deviceId;

  const currentAssistantProcessUrl =
    assistantProcesses?.[0]?.state?.currentPath;
  const isFreePlanBannerVisible =
    !!subscription?.products.includes(Plan.FREE) &&
    !!subscription &&
    !shouldShowBannerAssistant;
  const shouldShowNoDevicesBanner = !isLoadingDevices && devices?.length === 0;
  const deviceCreationUrl = buildDeviceManagementSettingsPath({
    orgId,
    modalPanel: "import",
  });
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(
    () =>
      new Set(
        SCHEDULED_BANNERS.filter(
          (b) => localStorage.getItem(b.localStorageKey) === "true"
        ).map((b) => b.key)
      )
  );

  const banners: TopBanner[] = [];

  if (shouldShowBannerAssistant) {
    banners.push({
      key: "assistant-process",
      bannerText: "You have an ongoing assistant process.",
      buttonText: "Continue assistant",
      buttonLink: fillRouteWithSlugs(
        currentAssistantProcessUrl && isAssistantProcessInProgress
          ? (currentAssistantProcessUrl as ROUTES)
          : ROUTES.ASSISTANT,
        {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        }
      ),
      color: "info",
      rightText: "",
    });
  }

  if (shouldShowNoDevicesBanner) {
    banners.push({
      key: "no-devices",
      bannerText: "Create a device to get started",
      buttonText: "Create a device",
      buttonLink: deviceCreationUrl,
      color: "error",
      rightText: "",
    });
  }

  for (const scheduled of SCHEDULED_BANNERS) {
    if (
      Date.now() < scheduled.endTime &&
      !dismissedBanners.has(scheduled.key)
    ) {
      banners.push({
        key: scheduled.key,
        bannerText: scheduled.bannerText,
        color: scheduled.color,
        rightText: "",
        onClose: () => {
          localStorage.setItem(scheduled.localStorageKey, "true");
          setDismissedBanners((prev) => new Set(prev).add(scheduled.key));
        },
      });
    }
  }

  if (isFreePlanBannerVisible) {
    banners.push({
      key: "free-plan",
      bannerText:
        "Upgrade to a premium plan to complete your device's CE mark or FDA submission.",
    });
  }

  const hasTopBannerOffset = banners.length > 0;
  const bannerStackHeight = getBannerHeightValue(banners.length);
  const mainContentSheetHeightClassName = hasTopBannerOffset
    ? "h-[calc(100%_-_var(--banner-stack-height)_-_16px)]"
    : "h-[calc(100%_-_16px)]";
  const isDashboardRoute = Boolean(
    matchPath({ path: ROUTES.DASHBOARD, end: true }, currentPath)
  );
  const mainContentSheetOverflowClassName = isDashboardRoute
    ? "overflow-hidden"
    : "overflow-y-auto";
  const mainContentOverflowClassName = isDashboardRoute
    ? "overflow-hidden"
    : "overflow-y-auto";
  const bannerStackStyle = {
    "--banner-stack-height": bannerStackHeight,
  } as CSSProperties;

  const {
    uiStore: {
      isLeftPanelCollapsed,
      isRightPanelOpen,
      setIsLeftPanelCollapsed,
      setIsRightPanelOpen,
    },
  } = useStore();
  const canRenderRightPanel =
    isRightPanelOpen &&
    isAuthenticated &&
    (!!deviceId || isRightPanelOrgRoute(currentPath));
  const appLayoutRef = useRef<HTMLDivElement>(null);
  const leftDrawerPanelRef = useRef<ImperativePanelHandle>(null);
  const drawerWidthModeRef = useRef<LeftDrawerWidthMode>(
    isLeftPanelCollapsed ? "collapsed" : "reset"
  );
  const pendingProgrammaticDrawerModeRef = useRef<LeftDrawerWidthMode | null>(
    null
  );
  const [appLayoutWidth, setAppLayoutWidth] = useState(() =>
    getFallbackPanelWidth()
  );

  const leftDrawerCollapsedSize = useMemo(
    () =>
      pixelWidthToPercentage(LEFT_DRAWER_COLLAPSED_WIDTH_PX, appLayoutWidth),
    [appLayoutWidth]
  );
  const leftDrawerOpenSize = useMemo(
    () =>
      Math.max(
        pixelWidthToPercentage(LEFT_DRAWER_DEFAULT_WIDTH_PX, appLayoutWidth),
        leftDrawerCollapsedSize
      ),
    [appLayoutWidth, leftDrawerCollapsedSize]
  );

  const resizeLeftDrawer = useCallback(
    (mode: Exclude<LeftDrawerWidthMode, "custom">) => {
      const leftDrawerPanel = leftDrawerPanelRef.current;
      if (!leftDrawerPanel) {
        return;
      }

      pendingProgrammaticDrawerModeRef.current = mode;

      if (mode === "collapsed") {
        leftDrawerPanel.collapse();
        return;
      }

      leftDrawerPanel.resize(leftDrawerOpenSize);
    },
    [leftDrawerOpenSize]
  );

  const handleLeftDrawerCollapse = useCallback(() => {
    drawerWidthModeRef.current = "collapsed";

    if (!isLeftPanelCollapsed) {
      setIsLeftPanelCollapsed(true);
    }
  }, [isLeftPanelCollapsed, setIsLeftPanelCollapsed]);

  const handleLeftDrawerExpand = useCallback(() => {
    const pendingMode = pendingProgrammaticDrawerModeRef.current;

    if (pendingMode === "reset") {
      drawerWidthModeRef.current = "reset";
      return;
    }

    drawerWidthModeRef.current = "custom";

    if (isLeftPanelCollapsed) {
      setIsLeftPanelCollapsed(false);
    }
  }, [isLeftPanelCollapsed, setIsLeftPanelCollapsed]);

  const handleLeftDrawerResize = useCallback(
    (size: number) => {
      const pendingMode = pendingProgrammaticDrawerModeRef.current;
      if (pendingMode) {
        drawerWidthModeRef.current = pendingMode;
        pendingProgrammaticDrawerModeRef.current = null;
        return;
      }

      const drawerWidth = percentageToPixelWidth(size, appLayoutWidth);
      const collapseThreshold = LEFT_DRAWER_COLLAPSED_WIDTH_PX + 1;

      if (drawerWidth <= collapseThreshold) {
        drawerWidthModeRef.current = "collapsed";

        if (!isLeftPanelCollapsed) {
          setIsLeftPanelCollapsed(true);
        }

        return;
      }

      drawerWidthModeRef.current = "custom";

      if (isLeftPanelCollapsed) {
        setIsLeftPanelCollapsed(false);
      }
    },
    [appLayoutWidth, isLeftPanelCollapsed, setIsLeftPanelCollapsed]
  );

  useEffect(() => {
    const updateLayoutWidth = () => {
      setAppLayoutWidth(getPanelContainerWidth(appLayoutRef.current));
    };

    updateLayoutWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateLayoutWidth);
      return () => {
        window.removeEventListener("resize", updateLayoutWidth);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateLayoutWidth();
    });

    if (appLayoutRef.current) {
      resizeObserver.observe(appLayoutRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isLeftPanelCollapsed) {
      drawerWidthModeRef.current = "collapsed";
      resizeLeftDrawer("collapsed");
      return;
    }

    if (drawerWidthModeRef.current !== "custom") {
      drawerWidthModeRef.current = "reset";
      resizeLeftDrawer("reset");
    }
  }, [isLeftPanelCollapsed, resizeLeftDrawer]);

  return (
    <div className="flex h-full bg-color-bg" style={bannerStackStyle}>
      <AppVersionUpdatePrompt />
      {banners.map(({ key, ...banner }, index) => (
        <Banner key={key} {...banner} topOffset={getBannerHeightValue(index)} />
      ))}

      <div ref={appLayoutRef} className="flex min-w-0 flex-1">
        <PanelGroup direction="horizontal" className="h-full w-full">
          <Panel
            ref={leftDrawerPanelRef}
            id="left-drawer-panel"
            order={0}
            data-testid="left-drawer-panel"
            collapsible
            collapsedSize={leftDrawerCollapsedSize}
            defaultSize={
              isLeftPanelCollapsed
                ? leftDrawerCollapsedSize
                : leftDrawerOpenSize
            }
            minSize={leftDrawerCollapsedSize}
            onCollapse={handleLeftDrawerCollapse}
            onExpand={handleLeftDrawerExpand}
            onResize={handleLeftDrawerResize}
            className="min-w-0"
          >
            <LeftDrawer hasBanner={hasTopBannerOffset} />
          </Panel>
          <PanelResizeHandle
            data-testid="left-drawer-resize-handle"
            className="group relative w-[6px] shrink-0 cursor-col-resize bg-transparent transition-colors after:absolute after:left-1/2 after:top-0 after:h-full after:w-px after:-translate-x-1/2 after:bg-gray-200 group-hover:after:bg-gray-500"
          />
          <Panel
            id="app-content-panel"
            order={1}
            minSize={leftDrawerCollapsedSize}
            className="mr-2"
          >
            <div
              data-testid="main-content-sheet"
              className={`relative flex w-full min-h-0 min-w-0 flex-col ${mainContentSheetOverflowClassName} rounded border-[0.5px] border-solid border-slate-200 bg-color-surface mr-2 mb-2 ml-0 ${mainContentSheetHeightClassName} ${
                hasTopBannerOffset
                  ? "mt-[calc(var(--banner-stack-height)_+_8px)]"
                  : "mt-2"
              }`}
              style={bannerStackStyle}
            >
              <PanelGroup direction="horizontal" className="min-h-0 flex-1">
                <Panel
                  id="main-panel"
                  order={0}
                  defaultSize={isRightPanelOpen ? 50 : 100}
                  minSize={25}
                  className="min-h-0"
                >
                  <main
                    className={`h-full min-h-0 w-full ${mainContentOverflowClassName}`}
                  >
                    <Sentry.ErrorBoundary
                      fallback={(props) => <Fallback {...props} />}
                    >
                      {props.children || <Outlet />}
                    </Sentry.ErrorBoundary>
                  </main>
                </Panel>
                {canRenderRightPanel && (
                  <>
                    <PanelResizeHandle className="w-[1px] bg-gray-200 hover:bg-gray-500 transition-colors" />
                    <Panel
                      id="right-panel"
                      order={1}
                      defaultSize={50}
                      minSize={25}
                    >
                      <RightColumn onClose={() => setIsRightPanelOpen(false)} />
                    </Panel>
                  </>
                )}
              </PanelGroup>
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {onboardingIsOpen && !!user && !!org && (
        <Dialog
          className="rounded"
          open
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{
            "& .MuiPaper-root": {
              maxWidth: "100%",
            },
          }}
        >
          <Welcome closeModal={closeWelcomeModal} />
        </Dialog>
      )}
      {!onboardingIsOpen && <ChangelogModal />}
    </div>
  );
});
